import { Box, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import InterpretedTypeField from './InterpretedTypeField';
import PointCoordinateEditor from './PointCoordinateEditor';

/**
 * Docked summary for a structure: the editable interpreted type and
 * identification code, plus — for volumetric ROIs — approval status / volume /
 * algorithm; point ROIs additionally show the coordinate editor. The read-only
 * extras and contour statistics live in the (i) Details popover (see
 * StructureDetailsInformation).
 */
export default function StructureInformation({
    roi,
    onInterpretedTypeChange,
    onPointCoordinatesChange,
    isEditable,
    getRoiVolumeCm3,
}: {
    roi: RoiReadModel;
    onInterpretedTypeChange: (value: RoiInterpretedType) => void;
    onPointCoordinatesChange: (coords: {
        x: number;
        y: number;
        z: number;
    }) => void;
    isEditable: boolean;
    getRoiVolumeCm3: number;
}) {
    const intl = useIntl();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const liveRoi = currentStructureSet?.roiArray.find(
        (r) => r.roiNumber === roi.roiNumber
    );
    const liveCoordinatesMm =
        liveRoi?.pointCoordinatesMm ?? roi.pointCoordinatesMm;

    const volumeLabel = `${getRoiVolumeCm3.toFixed(2)} cm³`;

    return (
        <Stack>
            <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                sx={{ minWidth: 0, overflow: 'hidden' }}
            >
                <InterpretedTypeField
                    roi={roi}
                    isEditable={isEditable}
                    onChange={onInterpretedTypeChange}
                />
                {roi.identificationCode && (
                    <Box
                        display="flex"
                        gap={0.5}
                        overflow="hidden"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: 11, flexShrink: 0 }}
                        >
                            {intl.formatMessage({
                                id: 'structure.identificationCode',
                            })}
                            :
                        </Typography>
                        <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontSize: 11, overflow: 'hidden' }}
                            data-testid="structure-identification-code-value"
                        >
                            {roi.identificationCode.codeMeaning}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                {!roi.isPointRoi && (
                    <>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            <Box lineHeight={0}>
                                <ReviewStatus
                                    status={roi.approvalStatus}
                                    fontSize={11}
                                    color="text.secondary"
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                            >
                                /
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                                data-testid="structure-volume-value"
                            >
                                {volumeLabel}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                            >
                                /
                            </Typography>
                        </Box>
                    </>
                )}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: 11 }}
                >
                    {roi.roiGenerationAlgorithm}
                </Typography>
            </Box>
            {roi.isPointRoi && liveCoordinatesMm != null && (
                <>
                    <Box sx={{ my: 0.5 }} />
                    <PointCoordinateEditor
                        coordinatesMm={liveCoordinatesMm}
                        onChange={onPointCoordinatesChange}
                        disabled={!isEditable}
                    />
                </>
            )}
        </Stack>
    );
}
